/**
 * Wave 312 · Prumo slide-over queue.
 *
 * Right-anchored panel listing every open proposal. Triggered by the
 * top-bar pill or Cmd-Shift-A. Closes on Escape, click-outside, or
 * by clicking the X in the header.
 *
 * Sort: Tier 1 first (one-tap territory), then T2, then T3. Within
 * a tier, newest at the top — newest is the most likely "this is
 * relevant right now" decision.
 *
 * Empty state: a soft "queue zerada" message instead of a desperate
 * "no items found" — celebrating zero is a feature.
 *
 * Implementation notes:
 *   - Pure CSS slide. No framer-motion (the panel is simple enough
 *     that the bundle bloat isn't worth it; transition is plenty).
 *   - 320ms iOS drawer curve (cubic-bezier(0.32, 0.72, 0, 1))
 *     matches the emil-design rule for drawers.
 *   - aria-modal="true" + focus trap via inert on the rest of the
 *     app (handled by the parent that mounts this).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, type PrumoProposal } from '@/api/client';
import { useAuth } from '@/store/auth.store';
import { useUi } from '@/store/ui.store';
import { usePrumo } from '@/store/prumo.store';
import { useT } from '@/hooks/useT';
import { PrumoCard } from './PrumoCard';
import { PrumoMark } from './PrumoMark';
import { cn } from '@/lib/cn';
import {
  groupByBucket,
  defaultExpandedBucket,
  type PrumoBucket,
  type PrumoGroup,
} from '@/lib/prumo-triage';

// ─── Triage bucket UI constants ──────────────────────────────────────────────

const BUCKET_TITLE_PT: Record<PrumoBucket, string> = {
  approve_today: 'Aprovar hoje',
  blocked: 'Bloqueados',
  fyi: 'Para saber',
  background_health: 'Saúde da obra',
};

const BUCKET_TITLE_EN: Record<PrumoBucket, string> = {
  approve_today: 'Approve today',
  blocked: 'Blocked',
  fyi: 'Good to know',
  background_health: 'Project health',
};

function bucketTitle(id: PrumoBucket, isEn: boolean): string {
  return (isEn ? BUCKET_TITLE_EN : BUCKET_TITLE_PT)[id];
}

const BUCKET_STORAGE_KEY = (id: PrumoBucket) => `prumo.bucket.${id}.expanded`;

function readBucketExpanded(id: PrumoBucket, fallback: boolean): boolean {
  try {
    const raw = window.localStorage.getItem(BUCKET_STORAGE_KEY(id));
    if (raw === null) return fallback;
    return raw === 'true';
  } catch {
    return fallback;
  }
}

function writeBucketExpanded(id: PrumoBucket, value: boolean): void {
  try {
    window.localStorage.setItem(BUCKET_STORAGE_KEY(id), String(value));
  } catch {
    // Ignore — localStorage may be blocked.
  }
}

/**
 * Wave 351-C · `open` / `onClose` props are gone. The slide-over now
 * reads its visibility from the global `usePrumo` store so any
 * caller (PrumoPill, InboxPage, future command palette) can toggle
 * it through `openPrumo(proposalId?)` without prop-drilling.
 */
export function PrumoQueue() {
  const ref = useRef<HTMLDivElement>(null);
  const { locale } = useT();
  const isEn = locale === 'en';
  const user = useAuth((s) => s.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'project_manager';
  const [runStatus, setRunStatus] = useState<string | null>(null);
  const [tab, setTab] = useState<'pending' | 'history'>('pending');

  // Wave 351-C · global Prumo open state. `focusedProposalId` lights
  // up briefly on the matching card after the panel slides in.
  const open = usePrumo((s) => s.isOpen);
  const focusedProposalId = usePrumo((s) => s.focusedProposalId);
  const closePrumo = usePrumo((s) => s.closePrumo);
  const consumeFocus = usePrumo((s) => s.consumeFocus);
  const onClose = closePrumo;

  const proposals = useQuery({
    queryKey: ['prumo', 'proposals', tab],
    queryFn: () =>
      api.listPrumoProposals({
        limit: 50,
        // Pendentes also includes approved rows because executor
        // failures stay approved with lastOutcome.error and need retry.
        ...(tab === 'history'
          ? { status: 'executed,declined,expired' }
          : { status: 'proposed,approved' }),
      }),
    enabled: open,
    // Refetch when the panel opens — fast feedback if a proposal
    // landed since the last open.
    staleTime: 5_000,
  });

  // Wave 313+ — admin "rodar agora" so a demo doesn't have to wait
  // for 11 UTC. Runs all 3 proposers (PO + RDO + Medição) and refetches.
  // Banner explains all-zero results so JP knows whether (a) there's
  // nothing eligible (normal day) or (b) everything was already
  // proposed and is waiting in the queue (dedup).
  const runNow = useMutation({
    mutationFn: () => api.runPrumoProposers(),
    onSuccess: (res) => {
      const totalProposed =
        (res.po.proposed ?? 0) + (res.rdo.proposed ?? 0) + (res.medicao.proposed ?? 0);
      const totalScanned =
        (res.po.scanned ?? 0) + (res.rdo.scanned ?? 0) + (res.medicao.scanned ?? 0);
      const summary = isEn
        ? totalProposed > 0
          ? `Done: PO ${res.po.proposed ?? 0} · Daily log ${res.rdo.proposed ?? 0} · Measurement ${res.medicao.proposed ?? 0} — cards updated below.`
          : totalScanned > 0
            ? `Nothing new. Scanned PO ${res.po.scanned ?? 0} · Daily log ${res.rdo.scanned ?? 0} · Measurement ${res.medicao.scanned ?? 0} — all skipped (already proposed, outside criteria, or no data to draft).`
            : 'Nothing to scan. No draft POs, no active projects with signals, no phases with pending progress.'
        : totalProposed > 0
          ? `Pronto: PO ${res.po.proposed ?? 0} · RDO ${res.rdo.proposed ?? 0} · Medição ${res.medicao.proposed ?? 0} — cards atualizados abaixo.`
          : totalScanned > 0
            ? `Nada novo. Escaneou PO ${res.po.scanned ?? 0} · RDO ${res.rdo.scanned ?? 0} · Medição ${res.medicao.scanned ?? 0} — todos pulados (já propostos antes, fora dos critérios, ou sem dados pra rascunhar).`
            : 'Nada pra escanear. Sem POs draft, sem obras ativas com sinais, sem etapas com avanço pendente.';
      setRunStatus(summary);
      void proposals.refetch();
      window.setTimeout(() => setRunStatus(null), 9000);
    },
    onError: (e) => {
      setRunStatus((e as Error).message ?? 'falha ao rodar');
      window.setTimeout(() => setRunStatus(null), 6000);
    },
  });

  // Escape closes; click-outside closes (we mount a backdrop that
  // captures the click).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Wave 320 — broadcast Prumo open state so HouseAgent's floating mark,
  // HousyBubble, and the onboarding tour can step out of the way.
  // Without this, the casinha + tour beacons sit on top of the panel
  // header (z-index battles + ghost tooltips like the "Nova obra" pill
  // bleeding through). Reset on close, AND on unmount in case the panel
  // is torn down without going through onClose.
  const setPrumoOpen = useUi((s) => s.setPrumoOpen);
  useEffect(() => {
    setPrumoOpen(open);
  }, [open, setPrumoOpen]);
  useEffect(() => () => setPrumoOpen(false), [setPrumoOpen]);

  // Focus the panel on open so screen readers + keyboard users
  // land somewhere useful. Don't trap focus in this minimal pass —
  // we'll add an inert wrapper on the rest of the app in a follow-up.
  useEffect(() => {
    if (open) ref.current?.focus();
  }, [open]);

  // Wave 351-C · scroll-to-focused-proposal. When the inbox dispatches
  // `openPrumo(prumoCardId)` the panel mounts, the query resolves,
  // and the matching `<article data-proposal-id>` slides into view
  // with a 1.6s brand-ring highlight. After the highlight runs we
  // call `consumeFocus()` so a subsequent pill-click without an id
  // doesn't replay the animation against a stale value.
  useEffect(() => {
    if (!open || !focusedProposalId) return;
    if (proposals.isPending) return;
    const node = ref.current?.querySelector<HTMLElement>(
      `[data-proposal-id="${CSS.escape(focusedProposalId)}"]`,
    );
    if (!node) {
      // Card not in current page or not loaded yet — clear focus so
      // we don't keep retrying. The panel still opens on the queue
      // top, which is the safest fallback.
      consumeFocus();
      return;
    }
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    node.classList.add('ring-2', 'ring-brand-500/60', 'ring-offset-2', 'ring-offset-surface');
    const t = window.setTimeout(() => {
      node.classList.remove('ring-2', 'ring-brand-500/60', 'ring-offset-2', 'ring-offset-surface');
      consumeFocus();
    }, 1600);
    return () => window.clearTimeout(t);
  }, [open, focusedProposalId, proposals.isPending, proposals.data, consumeFocus]);

  const list = proposals.data ?? [];

  // ── Triage grouping ────────────────────────────────────────────────────────
  // Groups are recomputed whenever the proposal list changes. We feed
  // PrumoProposal objects directly — they carry `kind` and `tier` which
  // is all classifyPrumoProposal needs.
  const groups: PrumoGroup<PrumoProposal>[] = groupByBucket(list);

  // Per-bucket expansion state. Initialises from localStorage if a
  // preference was saved; falls back to the default expansion rule.
  const [expandedMap, setExpandedMap] = useState<Record<PrumoBucket, boolean>>(() => {
    // We need the groups to compute the default bucket, but groups depend
    // on `list` which is empty on first render (query hasn't resolved yet).
    // Bootstrap with fyi expanded; the effect below corrects this once
    // the query resolves.
    return {
      approve_today: readBucketExpanded('approve_today', false),
      blocked: readBucketExpanded('blocked', false),
      fyi: readBucketExpanded('fyi', true),
      background_health: readBucketExpanded('background_health', false),
    };
  });

  // Once the query resolves (or when list changes), apply the default
  // expansion rule for any bucket whose key has never been persisted.
  useEffect(() => {
    if (proposals.isPending || list.length === 0) return;
    const defaultBucket = defaultExpandedBucket(groups);
    setExpandedMap((prev) => {
      const next = { ...prev };
      let changed = false;
      // Only override buckets that have no stored preference yet.
      for (const b of ['approve_today', 'blocked', 'fyi', 'background_health'] as PrumoBucket[]) {
        const stored = window.localStorage?.getItem?.(BUCKET_STORAGE_KEY(b));
        if (stored === null || stored === undefined) {
          const shouldExpand = b === defaultBucket;
          if (next[b] !== shouldExpand) {
            next[b] = shouldExpand;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposals.isPending, list.length]);

  const toggleBucket = useCallback((id: PrumoBucket) => {
    setExpandedMap((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      writeBucketExpanded(id, next[id]);
      return next;
    });
  }, []);

  // Wave 320 (b) — portal to <body> so the panel escapes any ancestor that
  // creates a containing block for fixed descendants. The TopBar header
  // uses `backdrop-blur` (which sets `backdrop-filter`), and that single
  // property turns it into the containing block for every fixed child.
  // Result: `fixed inset-0` + `h-full` resolved to the 57px header
  // instead of the viewport, which is why the smoke run showed only the
  // panel's own header strip on screen. Rendering via portal sidesteps
  // the issue without forcing the TopBar to drop its blur effect.
  return createPortal(
    <>
      {/* Backdrop. Wave 320 — bumped to z-[100] so it sits above the
          HouseAgent floating mark (z-60), HouseAgent's own backdrop
          (z-70), and HouseAgent's open chat panel (z-80). The tour
          overlays (z-9998+) still win over everything when the user is
          actively in a tour, but TourOrchestrator now hides itself
          while Prumo is open so the tour can't leak the underlying-page
          tooltip ("Nova obra" ghost) through the panel. */}
      <button
        type="button"
        aria-label={isEn ? 'Close actions panel' : 'Fechar Prumo'}
        onClick={onClose}
        className={cn(
          // Wave 314f — bump from /30 → /50. Em modo claro o /30 ficava
          // muito sutil; dava pra "ler" a página atrás e o status pill da
          // obra parecia flutuar dentro do Prumo.
          'fixed inset-0 z-[100] bg-black/50 transition-opacity duration-300',
          open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
      />
      {/* Panel. z-[110] so it sits above its own backdrop.
          When closed: aria-hidden="true" hides the panel from assistive
          technology without removing the CSS transition target from the DOM.
          When open: role="dialog" + aria-modal="true" are added so screen
          readers and automation can discover the live region. This pattern
          avoids the always-mounted role="dialog" that was previously visible
          to AT even when the drawer was offscreen. */}
      <aside
        ref={ref}
        tabIndex={-1}
        {...(open
          ? { role: 'dialog' as const, 'aria-modal': true, 'aria-label': isEn ? 'Actions · suggestion queue' : 'Prumo · fila de ações' }
          : { 'aria-hidden': true })}
        className={cn(
          'fixed right-0 top-0 z-[110] flex h-full w-full max-w-[420px] flex-col',
          'border-l border-line bg-surface shadow-2xl',
          'transition-transform duration-[320ms]',
          'focus:outline-none',
          // iOS drawer curve — emil-design rule for drawers.
          // Wave 315 · token (--ease-drawer) em vez de cubic-bezier raw —
          // se o curve mudar globalmente, troca em globals.css só.
          open ? 'translate-x-0' : 'translate-x-full pointer-events-none',
          // Fix 4 (2026-05-17): when the aside is offscreen (translate-x-full)
          // Chromium still accounts for its 420px width in scrollWidth, causing
          // horizontal overflow on mobile (~390px viewport). overflow-x-clip
          // constrains the painted area without creating a new scroll context
          // (unlike overflow-x-hidden which breaks sticky children).
          !open && 'overflow-x-clip',
        )}
        style={{ transitionTimingFunction: 'var(--ease-drawer)' }}
      >
        {/* Header — Wave 320 polish: tighter title group (Mark + name +
            count read as one unit), action cluster on the right with
            consistent gap, and explicit min-w-0 so long counts never
            shove the buttons off the edge. */}
        <header className="flex items-center gap-3 border-b border-line px-4 py-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <PrumoMark size={18} active={list.length > 0} />
            <h2 className="text-[14px] font-semibold tracking-[-0.01em] text-ink">
              Prumo
            </h2>
            <span className="truncate text-[12px] tabular-nums text-ink-muted">
              {proposals.isPending
                ? '…'
                : list.length === 0
                  ? (isEn ? 'queue empty' : 'fila zerada')
                  : `${list.length} ${list.length === 1 ? (isEn ? 'pending' : 'pendente') : (isEn ? 'pending' : 'pendentes')}`}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {isAdmin && (
              <button
                type="button"
                onClick={() => runNow.mutate()}
                disabled={runNow.isPending}
                className="pressable inline-flex h-11 min-h-11 items-center rounded-md border border-line bg-surface px-2.5 text-[11px] font-medium text-ink-muted hover:bg-surface-2 hover:text-ink disabled:opacity-60"
                title={isEn ? 'Run all proposers now (admin)' : 'Rodar todos os proposers agora (admin)'}
              >
                {runNow.isPending ? (isEn ? 'Running…' : 'Rodando…') : (isEn ? 'Run now' : 'Rodar agora')}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="pressable inline-flex h-11 w-11 min-h-11 min-w-11 items-center justify-center rounded-md text-ink-muted hover:bg-surface-2 hover:text-ink"
              aria-label={isEn ? 'Close' : 'Fechar'}
            >
              <span aria-hidden className="text-[18px] leading-none">×</span>
            </button>
          </div>
        </header>
        {runStatus && (
          <div className="border-b border-line bg-surface-2 px-4 py-2 text-[11px] text-ink-muted">
            {runStatus}
          </div>
        )}

        {/* Tabs — Pendentes vs Histórico. Sits below the header so the
            run-status banner (if any) is closer to the action buttons. */}
        <div className="flex border-b border-line">
          {([
            { id: 'pending' as const, label: isEn ? 'Pending' : 'Pendentes' },
            { id: 'history' as const, label: isEn ? 'History' : 'Histórico' },
          ]).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'flex-1 border-b-2 px-3 py-2 text-[12px] font-medium transition-colors duration-150',
                tab === t.id
                  ? 'border-brand-500 text-ink'
                  : 'border-transparent text-ink-muted hover:text-ink',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-3">
          {proposals.isPending && (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-28 animate-pulse rounded-md border border-line bg-surface-2"
                />
              ))}
            </div>
          )}
          {!proposals.isPending && list.length === 0 && <EmptyState tab={tab} isEn={isEn} />}
          {!proposals.isPending && list.length > 0 && (
            <div className="space-y-1">
              {groups.map((group) => (
                <BucketSection
                  key={group.id}
                  group={group}
                  expanded={expandedMap[group.id]}
                  onToggle={() => toggleBucket(group.id)}
                  onDone={() => proposals.refetch()}
                  isEn={isEn}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer — keep airy; the queue carries the screen */}
        <footer className="border-t border-line px-4 py-2 text-[10px] uppercase tracking-[0.1em] text-ink-subtle">
          {isEn ? 'Cmd-Shift-A to open · Esc to close' : 'Cmd-Shift-A para abrir · Esc para fechar'}
        </footer>
      </aside>
    </>,
    document.body,
  );
}

// ─── BucketSection ────────────────────────────────────────────────────────────

function BucketSection({
  group,
  expanded,
  onToggle,
  onDone,
  isEn,
}: {
  group: PrumoGroup<PrumoProposal>;
  expanded: boolean;
  onToggle: () => void;
  onDone: () => void;
  isEn: boolean;
}) {
  const isEmpty = group.items.length === 0;

  if (isEmpty) {
    // Compact empty state: title + "0" muted; no chevron, no toggle.
    return (
      <section className="rounded-md border border-line/50">
        <div className="flex w-full items-center justify-between px-3 py-2">
          <span className="text-[12px] font-semibold text-ink-muted">
            {bucketTitle(group.id, isEn)}
          </span>
          <span className="text-[11px] tabular-nums text-ink-subtle">0</span>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-md border border-line">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-3 py-2 hover:bg-surface-2 transition-colors duration-100 rounded-t-md"
        aria-expanded={expanded}
      >
        <span className="text-[12px] font-semibold text-ink">
          {bucketTitle(group.id, isEn)}
        </span>
        <span className="flex items-center gap-1.5 text-[11px] tabular-nums text-ink-muted">
          <span aria-hidden>{expanded ? '▾' : '▸'}</span>
          <span>{group.items.length}</span>
        </span>
      </button>
      {expanded && (
        <ul className="divide-y divide-line/60 px-2 pb-2 pt-0.5 space-y-2 list-none">
          {group.items.map((p) => (
            <li key={p.id} className="list-none">
              <PrumoCard proposal={p} onDone={onDone} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── EmptyState ───────────────────────────────────────────────────────────────

function EmptyState({ tab, isEn }: { tab: 'pending' | 'history'; isEn: boolean }) {
  // "Queue empty" deserves a small reward, not a lonely empty state.
  // Tone is operational, not warm — Prumo doesn't get cute.
  if (tab === 'history') {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
        <PrumoMark size={28} />
        <p className="text-[14px] font-medium text-ink">{isEn ? 'No history yet.' : 'Sem histórico ainda.'}</p>
        <p className="max-w-[28ch] text-[12px] leading-snug text-ink-muted">
          {isEn
            ? 'When you approve or dismiss a card it appears here — with the time, outcome, and reason.'
            : 'Quando você aprovar ou recusar um cartão, ele aparece aqui — com a hora, o resultado e o motivo.'}
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
      <PrumoMark size={28} />
      <p className="text-[14px] font-medium text-ink">{isEn ? 'Queue empty.' : 'Fila zerada.'}</p>
      <p className="max-w-[28ch] text-[12px] leading-snug text-ink-muted">
        {isEn
          ? 'No pending actions right now. The system scans your projects every morning and adds cards here when something needs your approval.'
          : 'Sem ações pendentes agora. O Prumo varre as obras todo dia de manhã e coloca cards aqui quando algo precisar do seu OK.'}
      </p>
    </div>
  );
}
